import { Theme } from "./ThemeContext";

/**
 * Converts a hex color to RGB values
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

/**
 * Converts a hex color to RGBA string
 */
export const hexToRgba = (hex: string, alpha = 1): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

/**
 * Gets a CSS custom property string for a theme color
 */
export const getCssCustomProperty = (
  colorName: string,
  theme: Theme
): string => {
  return `var(--color-${colorName}, ${theme.colors[colorName as keyof typeof theme.colors].hex})`;
};

/**
 * Applies theme colors as CSS custom properties to a DOM element
 */
export const applyThemeToElement = (
  theme: Theme,
  element: HTMLElement = document.documentElement
): void => {
  Object.entries(theme.colors).forEach(([key, color]) => {
    element.style.setProperty(`--color-${key}`, color.hex);
  });
};

/**
 * Creates a theme object from a JSON theme file
 */
interface ThemeJson {
  name: string;
  author: string;
  colors: {
    primary: Record<string, string>;
    primaryvariant1: Record<string, string>;
    secondary: Record<string, string>;
    secondaryvariant1: Record<string, string>;
    secondaryvariant2: Record<string, string>;
    background: Record<string, string>;
    surface: Record<string, string>;
    accent1: Record<string, string>;
    accent2: Record<string, string>;
    accent3: Record<string, string>;
    accent4: Record<string, string>;
    accent5: Record<string, string>;
  };
}

export const createThemeFromJson = (themeJson: ThemeJson): Theme => {
  return {
    name: themeJson.name,
    author: themeJson.author,
    colors: themeJson.colors
  };
};
